import algoliaInsights from 'search-insights'
import { config } from '../../constants'
import MainModule from '../../modules/main'

// Add algoliaInsights to the window interface
declare global {
  interface Window {
    algoliaInsights?: typeof algoliaInsights
  }
}

// Types d'entités et leurs index correspondants basés sur ttt.json
const ENTITY_CONFIG = {
  event: {
    index: 'events_search',
    objectIdPrefix: 'eventOffers',
    pageTemplate: 'event',
    urlPattern: /\/\w+\/(evenement|event)\/[^\/]+\/\d+\/(\d+)/,
  },
  eventSeries: {
    index: 'eventseries_search',
    objectIdPrefix: 'eventSeries',
    pageTemplate: 'eventSeries',
    urlPattern: /\/\w+\/(serie-evenement|event-series)\/[^\/]+\/(\d+)/,
  },
  exhibition: {
    index: 'exhibitions_search',
    objectIdPrefix: 'exhibitions',
    pageTemplate: 'exhibition',
    urlPattern: /\/\w+\/(exposition|exhibition)\/[^\/]+\/(\d+)/,
  },
  festival: {
    index: 'festivals_search',
    objectIdPrefix: 'festivals',
    pageTemplate: 'festival',
    urlPattern: /\/\w+\/(festival)\/[^\/]+\/(\d+)/,
  },
  artist: {
    index: 'artists',
    objectIdPrefix: 'artists',
    pageTemplate: 'artist',
    urlPattern: /\/\w+\/(artiste|artist)\/[^\/]+\/(\d+)/,
  },
  place: {
    index: 'places_full',
    objectIdPrefix: 'places',
    pageTemplate: 'place',
    urlPattern: /\/\w+\/(lieu|place)\/[^\/]+\/(\d+)/,
  },
} as const

const algoliaAppId = config?.algolia?.appId || ''
const algoliaApiKey = import.meta.env.VITE_ALGOLIA_API_KEY || ''
const mainModule = MainModule.getInstance()
let cookieConsent: boolean = false
let user: object | null = null

export const initAlgoliaInsights = async () => {
  if (!algoliaAppId || !algoliaApiKey) {
    console.warn('Algolia Insights not initialized: Missing appId or apiKey.')
    return
  }

  mainModule.getEE().on('lvc:user_state_changed', authHandler)
}

const authHandler = async () => {
  user = await mainModule.getAuth().loadProfile()
  init()
}

const init = () => {
  // Using a callback function to get a reference to the Axeptio SDK
  // https://support.axeptio.eu/hc/en-gb/articles/30301629739665-Axeptio-Javascript-SDK
  ;(window as any)._axcb = (window as any)._axcb || []
  ;(window as any)._axcb.push(function (sdk: any) {
    cookieConsent = sdk?.userPreferencesManager?.choices?.algolia_insight ?? false

    algoliaInsights('init', {
      appId: algoliaAppId,
      apiKey: algoliaApiKey,
      useCookie: sdk?.userPreferencesManager?.choices?.algolia_insight ?? false,
    })

    // Update algoliaInsights Cookie config if Axeptio cookie consent is modified
    sdk?.on('cookies:complete', (choices: { algolia_insight: boolean }) => {
      algoliaInsights('init', {
        partial: true, // Will update only useCookie
        useCookie: choices.algolia_insight ?? false,
      })
    })

    // Assign algoliaInsights to window
    ;(window as any).algoliaInsights = algoliaInsights

    Object.entries(ENTITY_CONFIG).forEach(([entityType, config]) => {
      trackEntityPageView(entityType, config)
    })

    setupTicketClickTracking()
  })
}

const getEntityInfoFromUrl = (): { id: string; type: keyof typeof ENTITY_CONFIG } | null => {
  const currentPath = window.location.pathname

  for (const [entityType, config] of Object.entries(ENTITY_CONFIG)) {
    const match = currentPath.match(config.urlPattern)
    if (match) {
      const id = match[2]
      return { id, type: entityType as keyof typeof ENTITY_CONFIG }
    }
  }

  return null
}

const trackEntityPageView = (
  entityType: string,
  config: (typeof ENTITY_CONFIG)[keyof typeof ENTITY_CONFIG]
) => {
  const entityPage = document.querySelector(`[data-page-template="${config.pageTemplate}"]`)

  if (entityPage) {
    const entityInfo = getEntityInfoFromUrl()

    if (entityInfo && entityInfo.type === entityType) {
      const objectID = `${config.objectIdPrefix}-${entityInfo.id}`

      window.algoliaInsights?.('viewedObjectIDs', {
        eventName: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Page View`,
        index: config.index,
        objectIDs: [objectID],
        authenticatedUserToken: (user as { id: string })?.id || '',
      })
    }
  }
}

const setupTicketClickTracking = () => {
  const eventOfferPage = document.querySelectorAll('[data-page-template="event"]')

  if (eventOfferPage) {
    const entity = getEntityInfoFromUrl()

    eventOfferPage.forEach(el => {
      const ticketButtons = el.querySelectorAll('[data-gtm-trigger-id="ticketButton"]')

      ticketButtons.forEach(button => {
        button.addEventListener('click', () => {
          if (entity) {
            const objectID = `eventOffers-${entity.id}`

            window.algoliaInsights?.('convertedObjectIDs', {
              eventName: 'Ticket Button Click on Event Page',
              index: 'events_search',
              objectIDs: [objectID],
              authenticatedUserToken: (user as { id: string })?.id || '',
            })
          }
        })
      })
    })
  }
}
